'use client';

import { EnvelopeIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { cn } from '@/shared/utils/cn';

import { useRecaptchaScript } from '../../hooks/useRecaptchaScript';

import { FormToastViewport, useFormSubmissionToast } from '../shared/FormSubmissionToast';
import NewsletterFrequencySelect from './NewsletterFrequencySelect';
import { FormSubmitError } from '../shared/configurable/mock-submit';
import { submitNewsletter } from './submitNewsletter';
import type { NewsletterFormCallbacks, NewsletterFormValues } from './useNewsletterForm';
import useNewsletterForm from './useNewsletterForm';

type PreviewScenario = 'empty' | 'partial' | 'complete';

const SCENARIO_VALUES: Record<PreviewScenario, NewsletterFormValues> = {
  empty: { name: '', email: '', frequency: '' },
  partial: { name: 'María González', email: '', frequency: '' },
  complete: { name: 'María González', email: 'maria@example.com', frequency: '2' },
};

export type NewsletterFormVariant = 'standalone' | 'embedded' | 'card';

export interface NewsletterFormSubmitProps {
  /**
   * Endpoint JSON al que se envía la suscripción. Cuando se define y no hay
   * `onSubmit`, el formulario resuelve reCAPTCHA y hace el POST por sí mismo.
   */
  submitUrl?: string;
  recaptchaSiteKey?: string;
  recaptchaAction?: string;
  source?: string;
  pageUrl?: string;
}

export interface NewsletterFormProps extends NewsletterFormCallbacks, NewsletterFormSubmitProps {
  /**
   * Variante de layout. 'embedded' se integra dentro del footer; 'standalone'
   * es la misma pieza a dos columnas fuera de él; 'card' apila encabezado y
   * campos en una tarjeta navy para convivir con contenido a su lado.
   */
  variant?: NewsletterFormVariant;
  /** Escenario de preview para precargar campos con datos de ejemplo. */
  previewScenario?: PreviewScenario;
  /** Título del encabezado propio ('standalone' y 'card'). */
  title?: string;
  /** Bajada del encabezado propio ('standalone' y 'card'). */
  description?: string;
}

const DEFAULT_TITLE = 'Mantente al día con las novedades de ADIPA';
const DEFAULT_DESCRIPTION =
  'Recibe ebooks, guías y recursos exclusivos para tu práctica profesional.';
const DEFAULT_RECAPTCHA_ACTION = 'newsletter';

const inputBaseClassName = cn(
  'h-11 w-full rounded-adipa-md border bg-adipa-footer-field-surface px-3.5 text-sm',
  'text-adipa-footer-field-text placeholder:text-adipa-footer-field-placeholder',
  'transition-[border-color,box-shadow] duration-adipa-fast ease-out focus:outline-none',
);

const inputNormalClassName =
  'border-adipa-footer-line-strong hover:border-adipa-footer-text-muted focus:border-cyan focus:ring-2 focus:ring-adipa-footer-focus-ring';
const inputErrorClassName =
  'border-adipa-error focus:border-adipa-error focus:ring-2 focus:ring-adipa-error-ring';
const labelClassName = 'text-xs font-semibold text-adipa-footer-text-secondary';
const errorClassName = 'mt-1 text-xs text-adipa-error';

const NewsletterForm = ({
  variant = 'standalone',
  previewScenario,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  submitUrl,
  recaptchaSiteKey,
  recaptchaAction = DEFAULT_RECAPTCHA_ACTION,
  source,
  pageUrl,
  onSubmit,
  onSuccess,
  onError,
}: NewsletterFormProps) => {
  useRecaptchaScript(onSubmit ? undefined : recaptchaSiteKey);
  const [serverErrorMessage, setServerErrorMessage] = useState<string | undefined>();

  const handleError = (error: unknown) => {
    setServerErrorMessage(error instanceof FormSubmitError ? error.message : undefined);
    onError?.(error);
  };

  const submitToUrl = submitUrl
    ? (values: NewsletterFormValues) =>
        submitNewsletter({
          pageUrl: pageUrl ?? window.location.href,
          recaptchaAction,
          recaptchaSiteKey,
          source,
          url: submitUrl,
          values,
        })
    : undefined;

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isInvalid,
    isSubmitting,
    status,
    values,
  } = useNewsletterForm({
    onSubmit: onSubmit ?? submitToUrl,
    onSuccess,
    onError: handleError,
    initialValues: previewScenario ? SCENARIO_VALUES[previewScenario] : undefined,
  });
  useFormSubmissionToast({
    status,
    success: {
      title: '¡Suscripción exitosa!',
      message: 'Pronto recibirás noticias de ADIPA.',
    },
    error: {
      title: 'No pudimos suscribirte',
      message: serverErrorMessage ?? 'Revisa tus datos o intenta nuevamente en unos segundos.',
    },
  });

  const isEmbedded = variant === 'embedded';
  const isCard = variant === 'card';

  return (
    <>
      <FormToastViewport />
      <section
        aria-labelledby="newsletter-title"
        className={cn(
          isEmbedded && 'border-adipa-footer-line mb-10 border-b pb-10',
          !isEmbedded && 'rounded-adipa-lg bg-adipa-footer-bg p-6 md:p-8',
          isCard && 'shadow-adipa-card',
        )}
      >
        <div
          className={cn(
            'grid gap-8',
            !isCard && 'md:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] md:gap-12 lg:gap-16',
          )}
        >
          <div className="space-y-3">
            <div className="border-adipa-footer-label/30 bg-adipa-footer-accent-surface inline-flex items-center gap-2 rounded-full border px-3 py-1">
              <EnvelopeIcon
                aria-hidden="true"
                className="text-adipa-footer-label"
                size={13}
                weight="bold"
              />
              <span className="text-adipa-newsletter-eyebrow text-adipa-footer-label font-semibold uppercase tracking-[0.16em]">
                Newsletter
              </span>
            </div>
            <h2
              className="text-adipa-newsletter-title text-adipa-footer-text-primary font-semibold leading-snug"
              id="newsletter-title"
            >
              {title}
            </h2>
            <p className="text-adipa-newsletter-copy text-adipa-footer-text-muted leading-relaxed">
              {description}
            </p>
          </div>

          <form noValidate className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className={labelClassName} htmlFor="newsletter-name">
                  Nombre{' '}
                  <span aria-hidden="true" className="text-adipa-error">
                    *
                  </span>
                </label>
                <input
                  className={cn(
                    inputBaseClassName,
                    isInvalid('name') ? inputErrorClassName : inputNormalClassName,
                  )}
                  id="newsletter-name"
                  minLength={2}
                  placeholder="Nombre completo"
                  type="text"
                  value={values.name}
                  onBlur={() => handleBlur('name')}
                  onChange={(event) => handleChange('name', event.target.value)}
                />
                {isInvalid('name') ? <p className={errorClassName}>{errors.name}</p> : null}
              </div>

              <div className="space-y-1.5">
                <label className={labelClassName} htmlFor="newsletter-email">
                  Correo electrónico{' '}
                  <span aria-hidden="true" className="text-adipa-error">
                    *
                  </span>
                </label>
                <input
                  className={cn(
                    inputBaseClassName,
                    isInvalid('email') ? inputErrorClassName : inputNormalClassName,
                  )}
                  id="newsletter-email"
                  placeholder="Correo electrónico"
                  type="email"
                  value={values.email}
                  onBlur={() => handleBlur('email')}
                  onChange={(event) => handleChange('email', event.target.value)}
                />
                {isInvalid('email') ? <p className={errorClassName}>{errors.email}</p> : null}
              </div>
            </div>

            <div className="grid items-end gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <span className={labelClassName} id="newsletter-frequency-label">
                  ¿Cuántos correos al mes deseas recibir?
                </span>
                <div className="relative">
                  <NewsletterFrequencySelect
                    id="newsletter-frequency"
                    inputBaseClassName={inputBaseClassName}
                    inputNormalClassName={inputNormalClassName}
                    labelId="newsletter-frequency-label"
                    value={values.frequency}
                    onChange={(nextValue) => handleChange('frequency', nextValue)}
                  />
                </div>
              </div>

              <button
                className={cn(
                  'inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2',
                  'rounded-adipa-lg border border-transparent',
                  'bg-adipa-primary text-adipa-primary-foreground px-6 text-sm font-semibold',
                  'shadow-adipa-card',
                  'duration-adipa-fast transition ease-out',
                  'hover:bg-adipa-primary-hover hover:shadow-adipa-console-panel hover:-translate-y-0.5',
                  'active:bg-adipa-primary-hover active:translate-y-0',
                  'focus-visible:outline-adipa-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                  'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
                  'disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0',
                )}
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        fill="currentColor"
                      />
                    </svg>
                    Suscribiendo...
                  </>
                ) : (
                  'Suscribirme'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default NewsletterForm;
